import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { defaultFilters } from '@shared/utils'
import { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query'

import type {
  CardWithId,
  PaginatedPage,
  PaginatedResponse,
  PipelineQueryOptions,
  QueryKeyFn,
  QueryParams,
  WithId
} from '@shared/types'

export const COLUMN_DROPPABLE_PREFIX = 'column-'
export const SCROLL_THRESHOLD = 1
export const KANBAN_PER_PAGE = 5

export const defaultExpandedCardId = {
  id: undefined,
  type: undefined
}

export const dropAnimationStyle = {
  duration: 150,
  easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
}

export const statusQueryOptions = {
  ordering: 'current_step',
  per_page: defaultFilters.per_page
}

export const dragSensorConfig = {
  pointer: {
    activationConstraint: {
      distance: 8
    }
  },
  keyboard: {
    coordinateGetter: sortableKeyboardCoordinates
  }
}

export const buildBaseQueryParams = (queryParams: QueryParams) => ({
  owner: queryParams?.owner,
  period_start: queryParams?.period_start,
  position: queryParams?.position,
  search: queryParams?.search,
  lead_type: queryParams?.lead_type,
  per_page: KANBAN_PER_PAGE
})

export const buildQueryKey = <TQueryOptions extends PipelineQueryOptions>(
  queryKeyFn: QueryKeyFn<TQueryOptions>,
  pipelineListQueryOptions: PipelineQueryOptions,
  statusId: number
) =>
  queryKeyFn({
    query: {
      ...pipelineListQueryOptions,
      per_page: KANBAN_PER_PAGE,
      status: statusId
    } as PipelineQueryOptions as TQueryOptions
  })

export const cardExistsInPages = <T extends WithId>(
  pages?: PaginatedPage<T>[],
  cardId?: string
): boolean => {
  if (!pages) return false
  return pages.some((page) =>
    page?.results?.some((card) => String(card?.id) === cardId)
  )
}

export const removeCardFromPages = <T extends WithId>(
  oldData?: { pages: PaginatedPage<T>[] },
  cardId?: string
) => {
  if (!oldData) return oldData

  return {
    ...oldData,
    pages: oldData.pages.map((page, index) => {
      const filtered = page.results.filter(
        (card) => String(card?.id) !== cardId
      )

      return {
        ...page,
        results: filtered,
        ...(index === 0 && typeof page.count === 'number'
          ? { count: Math.max(0, page.count - 1) }
          : {})
      }
    })
  }
}

export const addCardToPages = <T extends CardWithId>(
  oldData: { pages: PaginatedPage<T>[] },
  card?: T,
  statusId?: number
) => {
  if (!oldData || !card) return oldData

  const updatedCard = {
    ...card,
    status: {
      ...(typeof card?.status === 'object' && card.status ? card.status : {}),
      id: statusId,
      allowed_transition_statuses: undefined
    }
  } as T

  return {
    ...oldData,
    pages: oldData.pages.map((page, index) =>
      index === 0
        ? {
            ...page,
            results: [updatedCard, ...page.results],
            count: (page?.count ?? 0) + 1
          }
        : page
    )
  }
}

export const extractColumnId = (over?: {
  data?: { current?: { sortable?: { containerId?: unknown } } }
  id?: unknown
}) => {
  const rawId = over?.data?.current?.sortable?.containerId ?? over?.id

  if (rawId == null) return null

  const columnId = String(rawId).startsWith(COLUMN_DROPPABLE_PREFIX)
    ? String(rawId).slice(COLUMN_DROPPABLE_PREFIX.length)
    : rawId

  return Number.isNaN(Number(columnId)) ? null : Number(columnId)
}

export const toStatusIdNumber = (statusId?: number | string) => {
  if (typeof statusId === 'number') return statusId

  if (typeof statusId === 'string') return Number(statusId)

  return 0
}

export const getNextPageParam = <T>(lastPage: unknown) => {
  const page = lastPage as PaginatedResponse<T>

  if (typeof page?.next === 'number') return page.next

  if (typeof page?.next === 'string') return Number(page.next)
  return undefined
}

export const getQueryData = <TCard extends CardWithId>(
  queryClient: QueryClient,
  queryKey: QueryKey | null
): InfiniteData<PaginatedResponse<TCard>> | undefined => {
  if (!queryKey) return undefined

  return queryClient.getQueryData(queryKey)
}

export const findCardInQueryData = <TCard extends CardWithId>(
  queryData?: InfiniteData<PaginatedResponse<TCard>>,
  cardId?: string
) => {
  if (!queryData || !cardId) return undefined
  return queryData?.pages
    ?.flatMap((page) => page?.results)
    ?.find((card) => String(card?.id) === cardId)
}

export const updateQueryDataRemoveCard = <TCard extends CardWithId>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  cardId: string
) => {
  queryClient.setQueryData(
    queryKey,
    (oldData: InfiniteData<PaginatedResponse<TCard>>) => {
      return removeCardFromPages(oldData, cardId)
    }
  )
}

export const updateQueryDataAddCard = <TCard extends CardWithId>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  card?: TCard,
  statusId?: number
) => {
  queryClient.setQueryData(
    queryKey,
    (oldData: InfiniteData<PaginatedResponse<TCard>>) => {
      if (!oldData) {
        const baseData = {
          pages: [
            {
              results: [] as CardWithId[],
              count: 0,
              next: null,
              previous: null
            }
          ],
          pageParams: [1]
        } as unknown as InfiniteData<PaginatedResponse<TCard>>

        return addCardToPages(baseData, card, statusId)
      }
      if (cardExistsInPages(oldData.pages, String(card?.id))) {
        return oldData
      }
      return addCardToPages(oldData, card, statusId)
    }
  )
}

export const restoreCardToSource = <TCard extends CardWithId>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  card?: TCard,
  cardId?: string,
  statusId?: number
) => {
  queryClient.setQueryData(
    queryKey,
    (oldData: InfiniteData<PaginatedResponse<TCard>> | undefined) => {
      if (!oldData) return oldData
      if (cardExistsInPages(oldData.pages, cardId)) {
        return oldData
      }
      return addCardToPages(oldData, card, statusId)
    }
  )
}

export const findStatusInPages = <TStatus extends WithId>(
  pages?: PaginatedPage<TStatus>[],
  statusId?: number
) => {
  if (!pages) return undefined
  return pages
    .flatMap((page) => page?.results)
    .find((status) => status?.id === statusId)
}
